<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose when the waiting time rule applies.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charging</span>
                    <p class="section-heading__copy">Set included minutes, billing intervals and hourly rates.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Included time</h5>
                            <p class="charging-group__hint">Minutes included before additional charges apply.</p>
                            <div class="field-pair">
                                <form-group name="includedWaitingTime" label="Included">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.includedWaitingTime)"
                                        v-model="rateDetail.includedWaitingTime.operator"
                                        :value="rateDetail.includedWaitingTime.operator"
                                    />

                                    <md-input v-model.number="rateDetail.includedWaitingTime.value" />
                                </form-group>
                                <form-group name="waitTimeInterval" label="Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.waitTimeInterval)"
                                        v-model="rateDetail.waitTimeInterval.operator"
                                        :value="rateDetail.waitTimeInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.waitTimeInterval.value" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Rates</h5>
                            <p class="charging-group__hint">Define hourly and interval charges.</p>
                            <div class="field-pair">
                                <form-group name="ratePerHour" label="Per Hour">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.waitingTimeRatePerHour)"
                                        v-model="rateDetail.waitingTimeRatePerHour.operator"
                                        :value="rateDetail.waitingTimeRatePerHour.operator"
                                    />
                                    <md-input v-model.number="rateDetail.waitingTimeRatePerHour.value" />
                                </form-group>
                                <form-group name="WaitTimeRatePerInterval" label="Per Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.waitTimeRatePerInterval)"
                                        v-model="rateDetail.waitTimeRatePerInterval.operator"
                                        :value="rateDetail.waitTimeRatePerInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.waitTimeRatePerInterval.value" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Descriptor</h5>
                            <p class="charging-group__hint">Text shown on invoices or reports.</p>
                            <form-group name="waitTimeDescription" label="Description">
                                <md-input v-model.trim="rateDetail.waitTimeDescription" />
                            </form-group>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="saveForm()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { decimal, minLength, maxLength } from 'vuelidate/lib/validators';
/* eslint-disable import/extensions */
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { RuleValidDays } from '@/components';
/* eslint-enable import/extensions */
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateWaitTimeRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('includedWaitingTime');
        this.$_ensureChargeField('waitTimeInterval');
        this.$_ensureChargeField('waitingTimeRatePerHour');
        this.$_ensureChargeField('waitTimeRatePerInterval');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: null
            },
            rateChargingTypes: RATE_CHARGING_TYPES,
            localMessages: {
                required: '{attribute} is required.'
            }
        };
    },
    validations: {
        rateDetail: {
            waitingTimeRatePerHour: {
                value: {
                    decimal
                }
            },
            includedWaitingTime: {
                value: {
                    decimal
                }
            },
            waitTimeRatePerInterval: {
                value: {
                    decimal
                }
            },
            waitTimeInterval: {
                value: {
                    decimal
                }
            },
            waitTimeDescription: {
                minLength: minLength(1),
                maxLength: maxLength(100)
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    watch: {
        'rateDetail.includedWaitingTime.value': {
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.includes(':')) {
                    this.$set(
                        this.rateDetail.includedWaitingTime,
                        'value',
                        this.formatTimeSpan(newVal)
                    );
                }
            },
            immediate: true
        },
        'rateDetail.waitTimeInterval.value': {
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.includes(':')) {
                    this.$set(
                        this.rateDetail.waitTimeInterval,
                        'value',
                        this.formatTimeSpan(newVal)
                    );
                }
            },
            immediate: true
        }
    },
    mounted() {
        if (this.isUpdate) {
            this.normalizeTimespanFields();
        }
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
            }

            if (
                this.$v.rateDetail.waitingTimeRatePerHour.value.$model === null &&
                this.$v.rateDetail.includedWaitingTime.value.$model === null &&
                this.$v.rateDetail.waitTimeRatePerInterval.value.$model === null &&
                this.$v.rateDetail.waitTimeInterval.value.$model === null
            ) {
                return false;
            }

            return true;
        },
        normalizeTimespanFields() {
            this.convertTimeSpanFieldToMinutes('includedWaitingTime');
            this.convertTimeSpanFieldToMinutes('waitTimeInterval');
        },
        convertTimeSpanFieldToMinutes(fieldName) {
            const field = this.rateDetail[fieldName];
            if (!field) {
                return;
            }

            const { value } = field;

            if (value === null || value === undefined || value === '') {
                return;
            }

            if (typeof value === 'string' && value.includes(':')) {
                this.$set(field, 'value', this.formatTimeSpan(value));
            }
        },
        formatTimeSpan(time) {
            if (time === null || time === undefined || time === '') {
                return null;
            }

            if (typeof time === 'number') {
                return time;
            }

            let day = 0;
            let timePortion = time;

            if (typeof time === 'string' && time.includes('.')) {
                const [dayPart, ...rest] = time.split('.');
                const parsedDay = Number(dayPart);
                if (!Number.isNaN(parsedDay) && rest.length > 0) {
                    day = parsedDay;
                    timePortion = rest.join('.');
                }
            }

            const segments = String(timePortion).split(':');
            const hours = Number(segments[0] || 0);
            const minutes = Number(segments[1] || 0);
            const seconds = Number(segments[2] || 0);

            if ([hours, minutes, seconds].some((segment) => Number.isNaN(segment))) {
                return null;
            }

            const totalMinutes = day * 24 * 60 + hours * 60 + minutes + Math.floor(seconds / 60);

            return totalMinutes === 0 ? null : totalMinutes;
        },
        async saveForm() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.updatingRule = false;

            this.rateDetail.enableIncludedWaitingTime =
                this.$v.rateDetail.waitingTimeRatePerHour.value.$model !== null ||
                this.$v.rateDetail.waitTimeRatePerInterval.value.$model !== null;

            this.$_handleLoaderState(true, 'PROCESSING...');

            const type = this.$route.params.type
                .toLowerCase()
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
                .toLowerCase();

            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === type);

            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            this.rateDetail.includedWaitingTime = this.rateDetail.includedWaitingTime;
            this.rateDetail.waitTimeInterval = this.rateDetail.waitTimeInterval;

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>
